import { Fragment, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Container } from "@/components/container";
import { KeenIcon } from "@/components";
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from "@/partials/toolbar";
import { useLayout } from "@/providers";
import { useSnackbar } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useGetGuestVisibilityQuery,
  useUpdateGuestVisibilityMutation,
} from "@/services/GuestVisibilityService";
import { GuestVisibilityItem } from "@/types/GuestVisibility.type";

type ModuleGroup = {
  moduleId: string;
  moduleName: string;
  items: GuestVisibilityItem[];
};

const GUEST_VISIBILITY_PERMISSION = "guest_visibility_manage";

// The "at least one map" rule applies to exactly these 4 map feature_keys — not the broader
// "chart" category label, which is just a display/grouping convention and could drift over time.
const MAP_FEATURE_KEYS = [
  "boater.discovery.basic_map",
  "boater.discovery.finland_raster_map",
  "boater.discovery.finland_s57_map",
  "boater.discovery.estonia_s57_map",
];

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

// Derives a module id/name from a feature_key the same way SubscriptionPermissionsPage
// derives a module from a permission's unique_key (e.g. "boater.discovery.basic_map" -> "Boater Discovery").
const deriveModuleFromFeatureKey = (
  featureKey: string,
): { id: string; name: string } => {
  const parts = featureKey.split(".");
  if (parts.length >= 2) {
    return {
      id: `${parts[0]}.${parts[1]}`,
      name: `${capitalize(parts[0])} ${capitalize(parts[1])}`,
    };
  }
  return { id: "unknown", name: "Unknown Module" };
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "object" && error !== null) {
    const typedError = error as {
      data?: { message?: string };
      message?: string;
    };

    if (typedError.data?.message) {
      return typedError.data.message;
    }

    if (typedError.message) {
      return typedError.message;
    }
  }

  return fallback;
};

const GuestVisibilityConfiguration = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const intl = useIntl();

  const canRead = hasPermission(GUEST_VISIBILITY_PERMISSION);
  const canUpdate = hasPermission(GUEST_VISIBILITY_PERMISSION);

  const { data, isLoading, isFetching, refetch } = useGetGuestVisibilityQuery(
    undefined,
    { skip: !canRead },
  );

  const [updateGuestVisibility, { isLoading: isUpdating }] =
    useUpdateGuestVisibilityMutation();

  const [items, setItems] = useState<GuestVisibilityItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedModules, setExpandedModules] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const nextItems = data?.data ?? [];
    setItems(nextItems);

    setExpandedModules((prev) => {
      const next = { ...prev };
      nextItems.forEach((item) => {
        const { id: moduleId } = deriveModuleFromFeatureKey(item.feature_key);
        if (!(moduleId in next)) next[moduleId] = false;
      });
      return next;
    });
  }, [data]);

  const handleToggle = (featureKey: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.feature_key === featureKey
          ? { ...item, is_visible: !item.is_visible }
          : item,
      ),
    );
  };

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const areAllItemsSelected = (group: ModuleGroup): boolean =>
    group.items.length > 0 && group.items.every((item) => item.is_visible);

  const handleSelectAllInModule = (moduleId: string, isChecked: boolean) => {
    setItems((prev) =>
      prev.map((item) =>
        deriveModuleFromFeatureKey(item.feature_key).id === moduleId
          ? { ...item, is_visible: isChecked }
          : item,
      ),
    );
  };

  // Group items by the module derived from their feature_key (same derivation as
  // SubscriptionPermissionsPage) and filter by search term.
  const filteredGroups = useMemo(() => {
    const grouped = new Map<string, ModuleGroup>();
    items.forEach((item) => {
      const { id: moduleId, name: moduleName } = deriveModuleFromFeatureKey(
        item.feature_key,
      );
      if (!grouped.has(moduleId)) grouped.set(moduleId, { moduleId, moduleName, items: [] });
      grouped.get(moduleId)!.items.push(item);
    });

    const term = searchTerm.trim().toLowerCase();
    let groups = Array.from(grouped.values());
    if (term) {
      groups = groups
        .map((group) => {
          const moduleMatch = group.moduleName.toLowerCase().includes(term);
          const matchingItems = group.items.filter((item) =>
            item.label.toLowerCase().includes(term),
          );
          if (!moduleMatch && matchingItems.length === 0) return null;
          return {
            moduleId: group.moduleId,
            moduleName: group.moduleName,
            items: moduleMatch ? group.items : matchingItems,
          } as ModuleGroup;
        })
        .filter((group): group is ModuleGroup => group !== null);
    }

    return groups;
  }, [items, searchTerm]);

  // At least one of the 4 map feature_keys must stay visible to guests.
  const isChartVisibilityValid = useMemo(() => {
    const chartItems = items.filter((item) =>
      MAP_FEATURE_KEYS.includes(item.feature_key),
    );
    if (chartItems.length === 0) return true;
    return chartItems.some((item) => item.is_visible);
  }, [items]);

  const handleSave = async () => {
    if (!isChartVisibilityValid) {
      enqueueSnackbar(
        intl.formatMessage({ id: "GUEST_VISIBILITY.ERROR.AT_LEAST_ONE_MAP" }),
        { variant: "error" },
      );
      return;
    }

    try {
      await updateGuestVisibility({
        items: items.map(({ feature_key, is_visible }) => ({
          feature_key,
          is_visible,
        })),
      }).unwrap();
      enqueueSnackbar(
        intl.formatMessage({ id: "GUEST_VISIBILITY.SAVE_SUCCESS" }),
        { variant: "success" },
      );
      refetch();
    } catch (error: unknown) {
      const message = getErrorMessage(
        error,
        intl.formatMessage({ id: "GUEST_VISIBILITY.SAVE_ERROR" }),
      );
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  if (!canRead) {
    return (
      <Fragment>
        {currentLayout?.name === "demo1-layout" && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  {intl.formatMessage({ id: "GUEST_VISIBILITY.DESCRIPTION" })}
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500">
                {intl.formatMessage({ id: "GUEST_VISIBILITY.NO_PERMISSION" })}
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  const isBusy = isLoading || isFetching || isUpdating;

  return (
    <Fragment>
      {currentLayout?.name === "demo1-layout" && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="guest-visibility-configuration-heading" />
              <ToolbarDescription>
                {intl.formatMessage({ id: "GUEST_VISIBILITY.DESCRIPTION" })}
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={isBusy || !canUpdate || !isChartVisibilityValid}
              >
                {isBusy
                  ? intl.formatMessage({ id: "GUEST_VISIBILITY.SAVING" })
                  : intl.formatMessage({ id: "GUEST_VISIBILITY.SAVE" })}
              </button>
            </ToolbarActions>
          </Toolbar>
          <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col gap-4">
            {isLoading && (
              <div
                className="text-center py-4"
                role="status"
                aria-live="polite"
              >
                {intl.formatMessage({ id: "GUEST_VISIBILITY.LOADING" })}
              </div>
            )}
            {!isLoading && items.length > 0 && (
              <div
                className="mb-2"
                role="search"
                aria-label={intl.formatMessage({
                  id: "GUEST_VISIBILITY.SEARCH_LABEL",
                })}
              >
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center"
                  htmlFor="search-guest-visibility"
                >
                  <KeenIcon
                    type="filled"
                    icon="magnifier"
                    className="text-gray-500"
                  />
                  <input
                    id="search-guest-visibility"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "GUEST_VISIBILITY.SEARCH_PLACEHOLDER",
                    })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                    role="searchbox"
                  />
                </label>
              </div>
            )}
            {!isLoading && items.length > 0 && filteredGroups.length === 0 && (
              <div className="py-6 px-4 text-gray-500">
                {intl.formatMessage({ id: "GUEST_VISIBILITY.NO_SEARCH_RESULTS" })}
              </div>
            )}
            {!isLoading &&
              filteredGroups.map((group) => (
                <div
                  key={group.moduleId}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div
                    className="bg-gray-200 p-4 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleModuleExpansion(group.moduleId)}
                  >
                    <div
                      className="flex flex-col"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center">
                        <input
                          id={`module-${group.moduleId}`}
                          type="checkbox"
                          className="checkbox checkbox-sm"
                          checked={areAllItemsSelected(group)}
                          disabled={isBusy || !canUpdate}
                          aria-label={`Select all in ${group.moduleName}`}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectAllInModule(
                              group.moduleId,
                              e.target.checked,
                            );
                          }}
                        />
                        <label
                          htmlFor={`module-${group.moduleId}`}
                          className="ml-3 text-lg font-medium text-gray-900 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                          id={`module-label-${group.moduleId}`}
                        >
                          {group.moduleName}
                        </label>
                      </div>
                      {group.items.some((item) =>
                        MAP_FEATURE_KEYS.includes(item.feature_key),
                      ) &&
                        !isChartVisibilityValid && (
                          <p
                            className="text-red-500 text-xs mt-1 ml-8 contrast:text-[#FF8B33]"
                            role="alert"
                          >
                            {intl.formatMessage({
                              id: "GUEST_VISIBILITY.ERROR.AT_LEAST_ONE_MAP",
                            })}
                          </p>
                        )}
                    </div>
                    <button
                      type="button"
                      className="text-gray-500 focus:outline-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleModuleExpansion(group.moduleId);
                      }}
                      aria-label={`${expandedModules[group.moduleId] ? "Collapse" : "Expand"} ${group.moduleName}`}
                      aria-expanded={Boolean(expandedModules[group.moduleId])}
                      aria-controls={`module-panel-${group.moduleId}`}
                    >
                      {expandedModules[group.moduleId] ? (
                        <KeenIcon type="filled" icon="up" />
                      ) : (
                        <KeenIcon type="filled" icon="down" />
                      )}
                    </button>
                  </div>

                  {expandedModules[group.moduleId] && (
                    <div
                      id={`module-panel-${group.moduleId}`}
                      role="region"
                      aria-labelledby={`module-label-${group.moduleId}`}
                      className="pl-10 divide-y divide-gray-200"
                    >
                      {group.items.map((item) => (
                        <label
                          key={item.feature_key}
                          className="py-3 px-4 flex items-center gap-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            checked={item.is_visible}
                            onChange={() => handleToggle(item.feature_key)}
                            disabled={isBusy || !canUpdate}
                            aria-label={item.label}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            {!isLoading && items.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                {intl.formatMessage({ id: "GUEST_VISIBILITY.EMPTY" })}
              </div>
            )}
          </div>
        </Container>
      )}
    </Fragment>
  );
};

export { GuestVisibilityConfiguration };
